import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, CompassIcon, ShieldIcon, TargetIcon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
const principles = ['الأطفال أولًا', 'الهوية مصدر قوة وليست عائقًا', 'يجب أن يحمي التعليم بقدر ما يعلّم', 'الاندماج لا يعني الذوبان'];
const challenges = ['ضغوط الهوية والعزلة الثقافية', 'التمييز والتنمر', 'نقص الدعم التعليمي المراعي للثقافة والهوية', 'محدودية الإرشاد للآباء والأمهات عند التعامل مع المدارس والمؤسسات'];
const governanceCommitments = ['فصل واضح بين الأنشطة ذات الرسالة المجتمعية والأنشطة التجارية', 'تقارير شفافة ومساءلة مسؤولة', 'التوافق مع أفضل الممارسات الدولية للمنظمات غير الحكومية والتعليمية'];
const teamValues = ['فهم عميق لحياة الأسر المسلمة في الدول غير المسلمة', 'خبرة مهنية في التعليم والعمل المجتمعي', 'التزام مشترك بحماية الأطفال ودعم الأسر'];
export function ArabicAbout() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-2197" data-mp="1jbx9g0a9tet3">
      <section id="who-we-are" className="relative overflow-hidden scroll-mt-24" data-id="element-2198" data-mp="1jbnedyadchgj">
        <GeometricAccent variant="dots" className="absolute right-6 top-10 h-32 w-32" opacity={0.4} data-id="element-2199" data-mp="1xzi9flbumxws" />
        <Container className="relative pb-8 pt-8 md:pb-12 md:pt-12" data-id="element-2200" data-mp="1t42xiyxejc30">
          <div className="grid items-start gap-10 lg:grid-cols-12" data-id="element-2201" data-mp="15cnzvjgkliy3">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-2202" data-mp="10duphzwky825">
              <span className="mb-3 inline-block text-[11px] font-semibold tracking-[0.12em] text-forest-700" data-id="element-2203" data-mp="1a9xqv3jly2o1">
                من نحن
              </span>
              <p className="mb-4 font-display text-2xl italic text-sand-500" data-id="element-2204" data-mp="2lr5qlbjb6ulv">
                بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
              </p>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 md:text-6xl" data-id="element-2205" data-mp="1ihkwl8zrz4c9">
                المركز الدولي
                <br data-id="element-2206" data-mp="1sepm9istxhxt" />
                للتعلّم الإسلامي
              </h1>
              <p className="mt-6 leading-loose text-ink-700" data-id="element-2207" data-mp="1wezpinp8lu8z">
                المركز الدولي للتعلّم الإسلامي منصة تعليمية عالمية صُممت لدعم الأسر المسلمة عبر الحدود. نعمل عند تقاطع التعليم والهوية والاندماج، ونتعامل مع التحديات الفريدة التي يواجهها الأطفال والآباء المسلمون في البيئات غير المسلمة.
              </p>
              <p className="mt-6 leading-loose text-ink-700" data-id="element-2208" data-mp="18pf5s2l9esyn">
                تُقدَّم برامجنا عبر الإنترنت ومن خلال المؤسسات الشريكة، بما يتيح لنا الوصول إلى الأسر في أوروبا وأمريكا الشمالية ومناطق أخرى تحتاج فيها الأقليات المسلمة إلى دعم منظّم وحسّاس ثقافيًا.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative h-fit self-start lg:col-span-6" data-id="element-2209" data-mp="kzb2xxfwax8p">
              <div className="relative left-0 aspect-[200/148] w-full lg:left-16 lg:w-[calc(100%+6rem)]" data-id="element-2210" data-mp="4fn894ufdjg5">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/aCgn5fRf4NDJanaSW5LeLk/About-Us-2.jpg" alt="أم مسلمة وطفلاها خلال درس مباشر عبر الإنترنت في المنزل" data-id="element-2211" data-mp="3vmpx3pmsjdn7" />
              </div>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="mt-10 max-w-none" data-id="element-2212" data-mp="3gx0t7mnbcvj8">
            <p className="font-semibold text-forest-700" data-id="element-2213" data-mp="37vatltja2qad">لسنا مدرسة تقليدية.</p>
            <p className="mt-2 leading-loose text-ink-700" data-id="element-2214" data-mp="32x9bpbxl8x0h">
              نحن مركز تعلّم يربط بين التعليم والأسر والمهنيين والمجتمعات حول رسالة مشتركة.
            </p>
          </motion.div>
        </Container>
      </section>

      <section id="vision-mission" className="relative overflow-hidden bg-sage-50/60 py-16 scroll-mt-24 md:py-24" data-id="element-2215" data-mp="1bsxyxs1i4ds7">
        <Container data-id="element-2216" data-mp="x2gxi8ov9nvb">
          <h2 className="mb-10 text-center font-display text-3xl font-semibold leading-[1.5] text-ink-900 md:text-5xl" data-id="element-2217" data-mp="123o5o8ux8p2i">
            الرؤية والرسالة
          </h2>
          <div className="grid items-stretch gap-12 lg:grid-cols-12" data-id="element-2218" data-mp="o2bro2zbmxj9">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-5" data-id="element-2219" data-mp="s5qfhfti4i79">
              <div className="aspect-[4/5] overflow-hidden rounded-3xl shadow-card lg:h-full lg:aspect-auto" data-id="element-2220" data-mp="28jzwtpzbt6ol">
                <img src="https://cdn.magicpatterns.com/uploads/v3TjHD3rav5saTSn5G19iR/Vision3.jpg" alt="أطفال مسلمون يتعاونون في مشروع تعليمي داخل صف حديث" className="h-full w-full object-cover" data-id="element-2221" data-mp="2djcpircvp7z3" />
              </div>
            </motion.div>
            <div className="lg:col-span-7" data-id="element-2222" data-mp="bmccqgcmzln1">
              <div className="space-y-4" data-id="element-2223" data-mp="bndpcp9awg6v">
                <InfoCard icon={CompassIcon} label="رؤيتنا" data-id="element-2224" data-mp="6uasa75beolx">
                  عالم يستطيع فيه الأطفال المسلمون النمو والتعلّم والازدهار بثقة وكرامة، بغض النظر عن البلد الذي يعيشون فيه.
                </InfoCard>
                <InfoCard icon={TargetIcon} label="رسالتنا" data-id="element-2225" data-mp="flumcfz0caak">
                  تمكين الأطفال والأسر المسلمة في الدول غير المسلمة من خلال تعليم عالي الجودة، ودعم للأسرة، وتعلّم قائم على المجتمع، مع تعزيز الاندماج دون فقدان الهوية.
                </InfoCard>
                <div className="rounded-2xl border border-sage-200 bg-white p-5 shadow-soft md:p-6" data-id="element-2226" data-mp="2wbi83881kazl">
                  <div className="flex items-start gap-4" data-id="element-2227" data-mp="2rcf2nmo5g2r3">
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-100 text-forest-700" data-id="element-2228" data-mp="2rdzwfa2n3hnw">
                      <ShieldIcon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2229" data-mp="3l262urp2d1pm" />
                    </span>
                    <div data-id="element-2230" data-mp="3q1ilf08wjmme">
                      <p className="mb-2 text-[11px] font-semibold tracking-[0.12em] text-forest-700" data-id="element-2231" data-mp="3q07i11jdcgvt">مبادئنا</p>
                      <ul className="space-y-2 text-ink-700" data-id="element-2232" data-mp="3v153bmzodr1j">
                        {principles.map(principle => <CheckItem key={principle} data-id="element-2233" data-mp="3ry7uci10jqi">{principle}</CheckItem>)}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section id="why-non-muslim-countries" className="relative overflow-hidden bg-white/60 py-16 scroll-mt-24 md:py-24" data-id="element-2234" data-mp="hkxnqflq0fyl">
        <Container data-id="element-2235" data-mp="mipbqyj8vhxi">
          <div className="grid items-stretch gap-12 lg:grid-cols-12" data-id="element-2236" data-mp="rjddae53nh5z">
            <div className="lg:col-span-6" data-id="element-2237" data-mp="rhisczttvp1w">
              <h2 className="font-display text-3xl font-semibold leading-[1.5] text-ink-900 md:text-5xl" data-id="element-2238" data-mp="2c1xnzyrafmfh">
                لماذا الدول
                <br data-id="element-2239" data-mp="270zx8xs7pk2d" />
                غير المسلمة؟
              </h2>
              <p className="mt-6 leading-loose text-ink-700" data-id="element-2240" data-mp="222onen1x39k5">
                تواجه الأسر المسلمة التي تعيش في الدول غير المسلمة تحديات لا تظهر غالبًا داخل أنظمة التعليم التقليدية.
              </p>
              <div className="mt-5 rounded-2xl border border-sage-200 bg-white p-6" data-id="element-2241" data-mp="2usf2aybdifhb">
                <p className="mb-4 font-medium text-ink-900" data-id="element-2242" data-mp="2psixwxyrkm18">تشمل هذه التحديات، على سبيل المثال لا الحصر:</p>
                <ul className="space-y-3" data-id="element-2243" data-mp="2l1kcjjyx4xr4">
                  {challenges.map(challenge => <CheckItem key={challenge} data-id="element-2244" data-mp="2g411su1fj33x">{challenge}</CheckItem>)}
                </ul>
              </div>
              <p className="mt-6 font-semibold leading-loose text-forest-700" data-id="element-2245" data-mp="3dk7vasqg00p1">
                أُنشئ المركز الدولي للتعلّم الإسلامي خصيصًا للاستجابة لهذه الحقائق.
              </p>
              <p className="mt-3 leading-loose text-ink-700" data-id="element-2246" data-mp="2h3alc2nwmjjy">
                من خلال تركيزنا على الدول غير المسلمة، نعالج المجالات التي تحتاج فيها الأسر والأطفال المسلمون إلى الدعم المنظم أكثر من غيرها، لنساعدهم على النجاح أكاديميًا واجتماعيًا وعاطفيًا، مع الحفاظ على الثقة بهويتهم.
              </p>
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="relative lg:col-span-6" data-id="element-2247" data-mp="2d39vllvculxl">
              <div className="aspect-[4/5] overflow-hidden rounded-3xl shadow-card ring-4 ring-ivory lg:h-full lg:aspect-auto" data-id="element-2248" data-mp="2qtdd5zjahrgp">
                <img src="https://cdn.magicpatterns.com/uploads/15TEHR9731k3AN2fQXXBYm/Non-Muslim-3.jpg" alt="طفل مسلم يجلس بمفرده في فناء مدرسة أوروبية" className="h-full w-full object-cover" data-id="element-2249" data-mp="2vvci3duogwfr" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section id="governance" className="relative overflow-hidden bg-[#F7EDE9]/60 py-16 scroll-mt-24 md:py-24" data-id="element-2250" data-mp="17wa59380f3zo">
        <Container data-id="element-2251" data-mp="1ltypc8u9673o">
          <div className="grid items-stretch gap-12 lg:grid-cols-12" data-id="element-2252" data-mp="1lugqnjvrvbig">
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="relative lg:col-span-5" data-id="element-2253" data-mp="1gx85rbj8otdi">
              <div className="aspect-[4/5] overflow-hidden rounded-3xl shadow-card ring-4 ring-sage-100 lg:h-full lg:aspect-auto" data-id="element-2254" data-mp="12xfkhvqhf5cx">
                <img src="https://cdn.magicpatterns.com/uploads/1dXjjxz3sa71ZgwQ3E21Jy/Governance.jpg" alt="امرأة مسلمة تراجع مستندات في مكتب حديث" className="h-full w-full object-cover" data-id="element-2255" data-mp="y4cq5ldvrrvo" />
              </div>
            </motion.div>
            <div className="lg:col-span-7" data-id="element-2256" data-mp="2t9c5lmub9om5">
              <h2 className="font-display text-3xl font-semibold leading-[1.5] text-ink-900 md:text-5xl" data-id="element-2257" data-mp="2obtw9vcdt7dm">
                الحوكمة
                <br data-id="element-2258" data-mp="2jbxs9w3opfy2" />
                والشفافية
              </h2>
              <p className="mt-4 font-semibold leading-loose text-forest-700" data-id="element-2259" data-mp="2fc6sx403drhx">
                الشفافية والمساءلة أساس عملنا.
              </p>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-2260" data-mp="2advh4nq8l6x2">
                يعمل المركز الدولي للتعلّم الإسلامي ضمن هياكل حوكمة واضحة ومعايير أخلاقية والتزام قوي بالاستخدام المسؤول للتمويل والموارد.
              </p>
              <p className="mt-4 font-medium text-ink-900" data-id="element-2261" data-mp="20kenbjq3nrte">نحرص على:</p>
              <ul className="mt-3 space-y-3 text-ink-700" data-id="element-2262" data-mp="1vp92ypccil7f">
                {governanceCommitments.map(commitment => <CheckItem key={commitment} data-id="element-2263" data-mp="1ro5l5xaqmf3k">{commitment}</CheckItem>)}
              </ul>
              <p className="mt-6 leading-loose text-ink-700" data-id="element-2264" data-mp="1mpcaecmu5273">
                صُمم نموذج الحوكمة لدينا لتلبية توقعات الجهات المانحة الدولية والشركاء المؤسسيين والمجتمعات التي نخدمها.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section id="leadership-team" className="relative overflow-hidden bg-sand-50/60 py-16 scroll-mt-24 md:py-24" data-id="element-2265" data-mp="b65bhrc8txlh">
        <Container data-id="element-2266" data-mp="g2m9red5psca">
          <div className="grid items-stretch gap-12 lg:grid-cols-12" data-id="element-2267" data-mp="3fju13f7igqjl">
            <div className="lg:col-span-6" data-id="element-2268" data-mp="3flokgp34walk">
              <h2 className="font-display text-3xl font-semibold leading-[1.5] text-ink-900 md:text-5xl" data-id="element-2269" data-mp="3jkxezw600ci6">
                القيادة
                <br data-id="element-2270" data-mp="3ojqicvrkmn4m" />
                والفريق
              </h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-2271" data-mp="3tayuw0o76om0">
                يجمع فريقنا بين المعلّمين والمتخصصين في نمو الأطفال وقادة المجتمع والمهنيين ذوي الخبرة الدولية.
              </p>
              <div className="mt-6 rounded-2xl border border-sage-200 bg-white p-6" data-id="element-2272" data-mp="30uo3fvobn8dt">
                <p className="mb-3 font-semibold text-ink-900" data-id="element-2273" data-mp="30sl60qkuacz2">يجمعنا:</p>
                <ul className="space-y-3 text-ink-700" data-id="element-2274" data-mp="7ymkp7hc5ild">
                  {teamValues.map(value => <CheckItem key={value} data-id="element-2275" data-mp="2z9xumqgsbis">{value}</CheckItem>)}
                </ul>
              </div>
              <div className="mt-6 flex items-start gap-4 rounded-2xl border border-sand-200 bg-sand-50 p-5" data-id="element-2276" data-mp="cr5vxxsjw1n0">
                <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-sand-200 bg-white p-2" data-id="element-2277" data-mp="3l6pkijia0vvq">
                  <img src="https://cdn.magicpatterns.com/uploads/hH9LkDBwayz7QC9rNa2gq8/iilh-emblem.png" alt="شعار المركز الدولي للتعلّم الإسلامي" className="h-full w-full object-contain" data-id="element-2278" data-mp="3g9h3wncs0zwl" />
                </div>
                <p className="text-sm leading-loose text-ink-700" data-id="element-2279" data-mp="19g53tbp831eo">
                  تُطوَّر جميع البرامج وتُقدَّم بواسطة مهنيين مؤهلين يلتزمون بقيمنا ومعاييرنا ورسالتنا.
                </p>
              </div>
            </div>
            <div className="lg:col-span-6" data-id="element-2280" data-mp="1d1grwd72wy5m">
              <div className="h-72 overflow-hidden rounded-2xl shadow-card ring-4 ring-ivory sm:h-96 lg:h-full" data-id="element-2281" data-mp="1h17oixjae8i2">
                <img src="https://cdn.magicpatterns.com/uploads/xdGpwtJ1ncCksqBGxjJbLd/Team.jpg" alt="ثلاث نساء مسلمات يتحدثن معًا في مساحة مجتمعية دافئة" className="h-full w-full object-cover" data-id="element-2282" data-mp="1327ypmk1fnkb" />
              </div>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}
function InfoCard({
  icon: Icon,
  label,
  children
}: {
  icon: typeof CompassIcon;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-sage-200 bg-white p-5 shadow-soft md:p-6" data-id="element-2283" data-mp="1qxkgnqz6oeap">
      <div className="flex items-start gap-4" data-id="element-2284" data-mp="2enxykoo20wrk">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-100 text-forest-700" data-id="element-2285" data-mp="2jfg0bexwj1dl">
          <Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-2286" data-mp="uu4z2qehylg8" />
        </span>
        <div data-id="element-2287" data-mp="qs1mfyg3datd">
          <p className="mb-1 text-[11px] font-semibold tracking-[0.12em] text-forest-700" data-id="element-2288" data-mp="qrib50ky5kob">{label}</p>
          <p className="leading-loose text-ink-700" data-id="element-2289" data-mp="lsf63g4yvegc">{children}</p>
        </div>
      </div>
    </div>;
}
function CheckItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-start gap-3 text-[15px] leading-loose text-ink-700" data-id="element-2290" data-mp="1ehcodj96hb93">
      <CheckCircle2Icon size={16} className="mt-1.5 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-2291" data-mp="1ejfavtb4gm92" />
      <span data-id="element-2292" data-mp="14rjecmoop46y">{children}</span>
    </li>;
}